// Shared header/footer + helpers for all e-shot templates.
//
// Modernised against current email best practice (sourced from React Email docs,
// Litmus / Email on Acid patterns):
//   • Inline styles only on every element. <style> blocks in <head> are kept
//     for two narrow purposes: dark-mode media queries (Apple Mail / iOS Mail
//     honor them) and a mobile-responsive @media (max-width: 640px) block.
//     Gmail/Outlook strip <style> but fall back to the inline values, so
//     nothing breaks for them.
//   • Tables for structural layout (Outlook still doesn't render flex/grid).
//     Inside cells we use semantic <h1>/<h2>/<p>/<a> with inline styles.
//   • Preheader text injected as a hidden span at the top of <body> so
//     mailbox previews are intentional, not "Hi {name}…" or some image alt.
//   • Bulletproof CTA buttons: VML <v:roundrect> for Outlook desktop wrapped
//     in <!--[if mso]> conditional comments, padded <a> for everyone else.
//   • 640px container, 32-40px section padding, 1.65 line-height, generous
//     vertical rhythm — feels modern rather than 2008 newsletter.
//   • Dark-mode pairs: background, surface, text, muted, divider. Apple
//     Mail / iOS Mail respect prefers-color-scheme; everywhere else keeps
//     light values.

// Slugify a send name for utm_campaign. Keeps lowercase letters,
// numbers and dashes; everything else becomes a single dash.
export function utmSlug(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'untitled-eshot';
}

// Walk the rendered HTML and append utm_* params to any trovex.com
// link. We deliberately scope to trovex.com domains so we don't pollute
// third-party links (the recipient's own systems, supplier portals,
// etc). Skipped if utm is empty.
export function appendUtmsToTrovexLinks(html, utm) {
  if (!html || !utm) return html;
  const params = {
    utm_source: utm.source || 'eshot',
    utm_medium: utm.medium || 'email',
    ...(utm.campaign ? { utm_campaign: utm.campaign } : {}),
    ...(utm.content ? { utm_content: utm.content } : {}),
    ...(utm.term ? { utm_term: utm.term } : {}),
  };
  return html.replace(/href=(["'])(https?:\/\/[^"']*trovex\.com[^"']*)\1/gi, (match, q, url) => {
    try {
      const u = new URL(url);
      // Don't override utm_* the author set explicitly.
      for (const [k, v] of Object.entries(params)) {
        if (!u.searchParams.has(k)) u.searchParams.set(k, v);
      }
      return `href=${q}${u.toString()}${q}`;
    } catch {
      return match;
    }
  });
}

export function htmlEscape(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Plain text → email-safe HTML paragraphs. Splits on blank lines, escapes
// each chunk, preserves single newlines as <br>.
export function paragraphsToHtml(text, fontFamily, options = {}) {
  if (!text) return '';
  const ff = fontFamily || 'Arial';
  const color = options.color || '#404040';
  return text
    .split(/\n\s*\n/)
    .filter((p) => p.trim())
    .map(
      (p) =>
        `<p style="margin: 0 0 18px; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 16px; line-height: 1.65; color: ${color};" class="dark-text">${htmlEscape(p).replace(/\n/g, '<br>')}</p>`,
    )
    .join('');
}

// Hidden preheader span — first ~90 chars surface as the inbox preview.
// Padding spaces stop Gmail concatenating subject + first visible text.
function preheaderSpan(text) {
  if (!text) return '';
  const safe = htmlEscape(text).slice(0, 140);
  return `<div style="display: none; max-height: 0; overflow: hidden; opacity: 0; mso-hide: all; font-size: 1px; line-height: 1px; color: transparent;">${safe}&zwnj;${'&nbsp;'.repeat(60)}</div>`;
}

export function buildHeader(branding, options = {}) {
  const b = branding || {};
  const accent = b.primaryColour || '#00897b';
  const logoNode = b.logoUrl
    ? `<img src="${htmlEscape(b.logoUrl)}" alt="${htmlEscape(b.companyName || 'Trovex')}" style="max-height: 44px; max-width: 60%; display: block; border: 0;" class="logo">`
    : `<span style="font-family: ${htmlEscape(b.fontFamily || 'Arial')}, Helvetica, Arial, sans-serif; font-size: 18px; font-weight: 700; color: #1a1a1a; letter-spacing: -0.01em;" class="dark-text">${htmlEscape(b.companyName || 'Trovex')}</span>`;
  const viewLink = options.viewInBrowserUrl
    ? `<a href="${htmlEscape(options.viewInBrowserUrl)}" style="font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #9ca3af; text-decoration: none;" class="muted">View in browser</a>`
    : '';
  return `
    <tr>
      <td style="padding: 28px 32px 20px; background-color: #ffffff;" class="surface">
        <table role="presentation" cellpadding="0" cellspacing="0" width="100%">
          <tr>
            <td valign="middle">${logoNode}</td>
            <td valign="middle" align="right">${viewLink}</td>
          </tr>
        </table>
      </td>
    </tr>
    <tr>
      <td style="padding: 0 32px; background-color: #ffffff;" class="surface">
        <div style="height: 3px; line-height: 3px; font-size: 0; background-color: ${htmlEscape(accent)};">&nbsp;</div>
      </td>
    </tr>`;
}

export function buildFooter(footer, branding, unsubscribeUrl, options = {}) {
  const f = footer || {};
  const b = branding || {};
  const ff = b.fontFamily || 'Arial';
  const socials = (f.socialLinks || [])
    .filter((s) => s && s.url)
    .map(
      (s) =>
        `<a href="${htmlEscape(s.url)}" style="font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 13px; color: ${htmlEscape(b.primaryColour || '#00897b')}; text-decoration: none; margin-right: 14px;" class="footer-link">${htmlEscape(s.platform || 'Link')}</a>`,
    )
    .join('');

  return `
    <tr>
      <td style="padding: 28px 32px; background-color: #fafafa; border-top: 1px solid #e5e7eb;" class="footer">
        ${socials ? `<p style="margin: 0 0 16px;">${socials}</p>` : ''}
        <p style="margin: 0 0 6px; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 13px; font-weight: 600; color: #1a1a1a; line-height: 1.45;" class="dark-text">${htmlEscape(f.companyName || 'Trovex')}</p>
        ${f.address ? `<p style="margin: 0 0 4px; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 12px; color: #6b7280; line-height: 1.55; white-space: pre-line;" class="muted">${htmlEscape(f.address)}</p>` : ''}
        ${f.companyNumber ? `<p style="margin: 0 0 12px; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 12px; color: #6b7280;" class="muted">Company no. ${htmlEscape(f.companyNumber)}</p>` : ''}
        <p style="margin: 16px 0 6px; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 12px; color: #6b7280;" class="muted">
          <a href="{{{ pm:unsubscribe }}}" style="color: #6b7280; text-decoration: underline;" class="muted-link">Unsubscribe</a>
        </p>
        ${f.copyrightLine ? `<p style="margin: 6px 0 0; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 11px; color: #9ca3af;" class="muted">${htmlEscape(f.copyrightLine)}</p>` : ''}
      </td>
    </tr>`;
}

// Wraps an email — handles <head>, dark mode <style>, mobile media query,
// 640px container, header + body + footer rows. The final HTML is run
// through appendUtmsToTrovexLinks so every trovex.com CTA carries the
// e-shot's campaign attribution (set by the caller via options.utm).
export function wrapEmail(innerHtml, branding, footer, options = {}) {
  const b = branding || {};
  const ff = b.fontFamily || 'Arial';
  const bg = b.bgColour || '#f3f4f6';
  const html = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width,initial-scale=1">
    <meta name="x-apple-disable-message-reformatting">
    <meta name="format-detection" content="telephone=no, date=no, address=no, email=no">
    <meta name="color-scheme" content="light only">
    <meta name="supported-color-schemes" content="light">
    <title>${htmlEscape(options.title || 'Trovex')}</title>
    <!--[if mso]>
      <noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript>
    <![endif]-->
    <style>
      a { color: inherit; }
      /* Force light appearance in clients that auto-invert (Outlook.com, dark-mode Gmail). */
      :root { color-scheme: light only; supported-color-schemes: light; }
      [data-ogsc] body, [data-ogsc] .email-bg, [data-ogsb] body, [data-ogsb] .email-bg { background-color: #f3f4f6 !important; }
      [data-ogsc] .surface, [data-ogsb] .surface { background-color: #ffffff !important; }
      [data-ogsc] .footer, [data-ogsb] .footer { background-color: #fafafa !important; }

      @media (max-width: 640px) {
        .email-container { width: 100% !important; max-width: 100% !important; }
        .email-body { padding: 28px 22px !important; }
        .header { padding: 22px 22px 16px !important; }
        .footer { padding: 22px 22px !important; }
        h1.headline { font-size: 24px !important; line-height: 1.25 !important; }
        h2.story-title { font-size: 18px !important; }
        .button-cell a { padding: 12px 18px !important; font-size: 15px !important; }
      }
    </style>
  </head>
  <body class="email-bg" style="margin: 0; padding: 0; background-color: ${htmlEscape(bg)}; font-family: ${ff}, Helvetica, Arial, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;">
    ${preheaderSpan(options.preheader)}
    <table role="presentation" cellpadding="0" cellspacing="0" width="100%" class="email-bg" style="background-color: ${htmlEscape(bg)};">
      <tr>
        <td align="center" style="padding: 32px 16px;">
          <table role="presentation" cellpadding="0" cellspacing="0" width="640" class="email-container" style="max-width: 640px; width: 100%; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.04);">
            ${buildHeader(branding, options)}
            <tr>
              <td class="surface email-body" style="padding: 36px 32px 12px; background-color: #ffffff;">
                ${innerHtml}
              </td>
            </tr>
            ${buildFooter(footer, branding, options.unsubscribeUrl, options)}
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
  return appendUtmsToTrovexLinks(html, options.utm);
}

// Bulletproof CTA button: VML <v:roundrect> for Outlook on Windows
// (renders as a real button with rounded corners and the brand fill);
// regular padded <a> for every other client.
export function ctaButton(label, url, branding) {
  if (!label || !url) return '';
  const colour = (branding && branding.primaryColour) || '#00897b';
  const ff = (branding && branding.fontFamily) || 'Arial';
  const safeLabel = htmlEscape(label);
  const safeUrl = htmlEscape(url);
  return `
    <table role="presentation" cellpadding="0" cellspacing="0" class="button-cell" style="margin: 24px 0;">
      <tr>
        <td>
          <!--[if mso]>
          <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${safeUrl}" style="height:48px;v-text-anchor:middle;width:220px;" arcsize="14%" stroke="f" fillcolor="${colour}">
            <w:anchorlock/>
            <center style="color:#ffffff;font-family:${ff},Helvetica,Arial,sans-serif;font-size:15px;font-weight:600;">${safeLabel}</center>
          </v:roundrect>
          <![endif]-->
          <!--[if !mso]><!-- -->
          <a href="${safeUrl}" style="display: inline-block; padding: 14px 26px; background-color: ${colour}; color: #ffffff; text-decoration: none; font-family: ${ff}, Helvetica, Arial, sans-serif; font-size: 15px; font-weight: 600; border-radius: 8px; letter-spacing: 0.01em;">${safeLabel}</a>
          <!--<![endif]-->
        </td>
      </tr>
    </table>`;
}

export function heroImage(url, alt, options = {}) {
  if (!url) return '';
  const radius = options.radius != null ? options.radius : 8;
  return `<img src="${htmlEscape(url)}" alt="${htmlEscape(alt || '')}" style="display: block; width: 100%; max-width: 100%; height: auto; margin: 0 0 24px; border: 0; border-radius: ${radius}px;">`;
}

export function divider(branding) {
  const accent = (branding && branding.accentColour) || '#e5e7eb';
  return `<div class="divider" style="height: 1px; line-height: 1px; font-size: 0; background-color: ${htmlEscape(accent)}; margin: 32px 0;">&nbsp;</div>`;
}

// Strict token-leak check used by previews + dispatch. Skips known
// personalisation tokens (firstName, etc.) — those are substituted later
// by LivePreview/dispatch via substituteTokens() in _tokens.js. Anything
// else surviving is a typo or unsupported placeholder; fail loud.
const KNOWN_TOKEN_IDS = new Set([
  'firstName', 'lastName', 'name', 'title', 'accountName', 'repName',
]);
export function assertNoUnsubstitutedTokens(html) {
  const re = /\{\{\s*([\w.-]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(html)) !== null) {
    if (!KNOWN_TOKEN_IDS.has(m[1])) {
      throw new Error(`Unsubstituted token in rendered HTML: ${m[0]}`);
    }
  }
}
